import { useCallback, useEffect, useRef, useState } from "react";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import type { ForeignNationalActivitySubscription } from "../../../../api/types";

export type ActivitySubscriptionState = {
  subscribed: boolean;
  subscriberCount: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  toggle: () => Promise<void>;
};

/** Whether the signed-in user follows this client's activity, with an optimistic toggle. */
export const useActivitySubscription = (
  clientId: string
): ActivitySubscriptionState => {
  const [state, setState] = useState<ForeignNationalActivitySubscription>({
    subscribed: false,
    subscriber_count: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!clientId) return undefined;
    setIsLoading(true);
    ForeignNationalsAPI.getActivitySubscription(clientId)
      .then((s) => {
        if (alive.current) setState(s);
      })
      .catch((e) => {
        if (alive.current)
          setError(e instanceof Error ? e.message : "Failed to load");
      })
      .finally(() => {
        if (alive.current) setIsLoading(false);
      });
    return () => {
      alive.current = false;
    };
  }, [clientId]);

  const toggle = useCallback(async () => {
    if (!clientId || isSaving) return;
    const previous = state;
    const next = !previous.subscribed;
    setIsSaving(true);
    setError(null);
    setState({
      subscribed: next,
      subscriber_count: Math.max(0, previous.subscriber_count + (next ? 1 : -1))
    });
    try {
      const saved = next
        ? await ForeignNationalsAPI.subscribeActivity(clientId)
        : await ForeignNationalsAPI.unsubscribeActivity(clientId);
      if (alive.current) setState(saved);
    } catch (e) {
      if (alive.current) {
        setState(previous);
        setError(e instanceof Error ? e.message : "Failed to update");
      }
    } finally {
      if (alive.current) setIsSaving(false);
    }
  }, [clientId, isSaving, state]);

  return {
    subscribed: state.subscribed,
    subscriberCount: state.subscriber_count,
    isLoading,
    isSaving,
    error,
    toggle
  };
};
